import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { store } from './store';
import LoginForm from './components/auth/LoginForm';
import ProtectedRoute from './components/auth/ProtectedRoute';
import SmartRedirect from './components/auth/SmartRedirect';
import DashboardLayout from './components/dashboard/DashboardLayout';
import VersionUpdateNotification from './components/VersionUpdateNotification';
import BackendStatusNotification from './components/BackendStatusNotification';
import { NotificationProvider } from './contexts/NotificationContext';
import { ThemeProvider } from './contexts/ThemeContext';

import TeamDashboard from './pages/TeamDashboard';
import CommissionsPage from './pages/CommissionsPage';
import CommissionsDiffPage from './pages/CommissionsDiffPage';
import EventsNew from './pages/EventsNew';
import Customers from './pages/Customers';
import CustomerDetail from './pages/CustomerDetail';
import SlingLocations from './pages/SlingLocations';
import SlingLocationDetail from './pages/SlingLocationDetail';
import SystemSettings from './pages/SystemSettings';
import UserManagement from './pages/UserManagement';
import PayrollAdmin from './pages/PayrollAdmin';
import EmailMarketing from './pages/EmailMarketing';
import ProfilePage from './pages/ProfilePage';
import UserSettings from './pages/UserSettings';
import TimesheetsPage from './pages/TimesheetsPage';
import FinancialReportsPage from './pages/FinancialReportsPage';
import ItemsAdmin from './pages/ItemsAdmin';
import SyncHistoryPage from './pages/SyncHistoryPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoiceDetailPage from './pages/InvoiceDetailPage';
import BankTransactionsPage from './pages/BankTransactionsPage';
import FormSubmissionsPage from './pages/FormSubmissionsPage';
import PaymentsPage from './pages/PaymentsPage';
import ApplicantsPage from './pages/ApplicantsPage';
import ApplicantDetailPage from './pages/ApplicantDetailPage';

import './assets/styles/custom.css';

// Create a React Query client with proper error handling
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: 'always',
      gcTime: 0,

      // Disable React Query retry - circuit breaker handles all retries
      // This prevents independent retry loops and coordinates all retry attempts
      retry: false,

      // RetryDelay kept for reference but unused since retry is disabled
      retryDelay: (attemptIndex) => {
        const baseDelay = Math.min(1000 * 2 ** attemptIndex, 30000);
        const jitter = Math.random() * 0.25 * baseDelay;
        return baseDelay + jitter;
      },
    },
  },
});

const AppContent: React.FC = () => {
  // Note: getCurrentUser is now handled by ProtectedRoute using React Query
  // This eliminates duplicate requests

  return (
    <Router>
      <div className="App">
        <Routes>
          <Route path="/login" element={<LoginForm />} />
          
          <Route path="/" element={
            <ProtectedRoute>
              <SmartRedirect />
            </ProtectedRoute>
          } />

          <Route path="/dashboard" element={
            <ProtectedRoute>
              <SmartRedirect />
            </ProtectedRoute>
          } />

          <Route path="/dashboard/team" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <TeamDashboard />
            </ProtectedRoute>
          } />

          <Route path="/commissions" element={
            <ProtectedRoute requiredRoles={['admin', 'finance', 'vp_sales', 'sales_manager', 'salesperson']}>
              <CommissionsPage />
            </ProtectedRoute>
          } />
          <Route path="/commissions/audit" element={
            <ProtectedRoute requiredRoles={['admin', 'vp_sales']}>
              <CommissionsDiffPage />
            </ProtectedRoute>
          } />
          <Route path="/commissions/:tab" element={
            <ProtectedRoute requiredRoles={['admin', 'finance', 'vp_sales', 'sales_manager', 'salesperson']}>
              <CommissionsPage />
            </ProtectedRoute>
          } />

          <Route path="/profile" element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          } />

          <Route path="/settings" element={
            <ProtectedRoute>
              <DashboardLayout 
                title="Settings"
                subtitle="Configure your preferences and account settings"
              >
                <UserSettings />
              </DashboardLayout>
            </ProtectedRoute>
          } />


          <Route path="/schedule" element={
            <ProtectedRoute requiredRoles={['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing']}>
              <EventsNew />
            </ProtectedRoute>
          } />

          <Route path="/customers" element={
            <ProtectedRoute requiredRoles={['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'finance', 'marketing']}>
              <Customers />
            </ProtectedRoute>
          } />

          <Route path="/customers/:id" element={
            <ProtectedRoute requiredRoles={['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'finance', 'marketing']}>
              <CustomerDetail />
            </ProtectedRoute>
          } />

          <Route path="/sling-locations" element={
            <ProtectedRoute requiredRoles={['sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing']}>
              <SlingLocations />
            </ProtectedRoute>
          } />

          <Route path="/sling/locations/:srcId/:locationId" element={
            <ProtectedRoute requiredRoles={['sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing']}>
              <SlingLocationDetail />
            </ProtectedRoute>
          } />

          <Route path="/financial-reports" element={
            <ProtectedRoute requiredRoles={['admin', 'vp_sales']}>
              <FinancialReportsPage />
            </ProtectedRoute>
          } />
          <Route path="/financial-reports/:tab" element={
            <ProtectedRoute requiredRoles={['admin', 'vp_sales']}>
              <FinancialReportsPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/users" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <UserManagement />
            </ProtectedRoute>
          } />

          <Route path="/admin/payroll" element={<Navigate to="/admin/payroll/activities" replace />} />
          <Route path="/admin/payroll/:tab" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <PayrollAdmin />
            </ProtectedRoute>
          } />

          <Route path="/invoices" element={
            <ProtectedRoute requiredRoles={['admin', 'salesperson', 'finance', 'sales_manager', 'vp_sales']}>
              <InvoicesPage />
            </ProtectedRoute>
          } />

          <Route path="/invoices/:id" element={
            <ProtectedRoute requiredRoles={['admin', 'salesperson', 'finance', 'sales_manager', 'vp_sales']}>
              <InvoiceDetailPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/items" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <ItemsAdmin />
            </ProtectedRoute>
          } />

          <Route path="/admin/sync-history" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <SyncHistoryPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/bank-transactions" element={
            <ProtectedRoute requiredRoles={['admin', 'finance']}>
              <BankTransactionsPage />
            </ProtectedRoute>
          } />

          <Route path="/form-submissions" element={
            <ProtectedRoute requiredRoles={['admin', 'marketing', 'salesperson', 'sales_manager', 'vp_sales', 'events_manager']}>
              <FormSubmissionsPage />
            </ProtectedRoute>
          } />

          <Route path="/applicants" element={
            <ProtectedRoute requiredRoles={['admin', 'events_manager']}>
              <ApplicantsPage />
            </ProtectedRoute>
          } />
          {/* Static tab paths outrank /applicants/:id in the router. */}
          <Route path="/applicants/email-log" element={
            <ProtectedRoute requiredRoles={['admin', 'events_manager']}>
              <ApplicantsPage />
            </ProtectedRoute>
          } />
          <Route path="/applicants/file-uploads" element={
            <ProtectedRoute requiredRoles={['admin', 'events_manager']}>
              <ApplicantsPage />
            </ProtectedRoute>
          } />
          <Route path="/applicants/:id" element={
            <ProtectedRoute requiredRoles={['admin', 'events_manager']}>
              <ApplicantDetailPage />
            </ProtectedRoute>
          } />

          <Route path="/invoice-payments" element={
            <ProtectedRoute requiredRoles={['admin', 'salesperson', 'finance', 'sales_manager', 'vp_sales']}>
              <PaymentsPage />
            </ProtectedRoute>
          } />

          <Route path="/admin/settings" element={<Navigate to="/admin/settings/general" replace />} />
          <Route path="/admin/settings/:tab" element={
            <ProtectedRoute requiredRoles={['admin']}>
              <SystemSettings />
            </ProtectedRoute>
          } />

          <Route path="/timesheets" element={<Navigate to="/timesheets/me" replace />} />
          <Route path="/timesheets/:tab" element={
            <ProtectedRoute>
              <TimesheetsPage />
            </ProtectedRoute>
          } />

          <Route path="/email-marketing" element={<Navigate to="/email-marketing/contacts" replace />} />
          <Route path="/email-marketing/:tab" element={
            <ProtectedRoute requiredRoles={['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing']}>
              <EmailMarketing />
            </ProtectedRoute>
          } />

          <Route path="*" element={
            <DashboardLayout>
              <div className="text-center py-5">
                <i className="bi bi-exclamation-triangle fs-1 text-warning mb-3 d-block"></i>
                <h3>Page Not Found</h3>
                <p className="text-muted">The page you're looking for doesn't exist.</p>
                <a href="/dashboard/team" className="btn btn-primary">
                  Go to Dashboard
                </a>
              </div>
            </DashboardLayout>
          } />
        </Routes>
      </div>
    </Router>
  );
};

const App: React.FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <Provider store={store}>
        <ThemeProvider>
          <NotificationProvider>
            <AppContent />
            <BackendStatusNotification />
            <VersionUpdateNotification />
          </NotificationProvider>
        </ThemeProvider>
      </Provider>
    </QueryClientProvider>
  );
};

export default App;